import React from 'react'
import styled from '@emotion/styled'
import { hexColors } from '@mobi/settings'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Money } from '@core/Components/Text/Money'
import { useObservableImmutable } from '@core/Utils/hooks/useObservableImmutable'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { useAppDispatch, useAppSelector } from '@core/Store/hooks'
import { setBonusDescriptionToDisplay, toggleIsBalanceVisible } from '@core/Areas/AppHeader/Store'
import { trackSideMenuItemClick } from '@core/Areas/AppHeader/helpers/analytics'
import { trackBonusBetsInfoClick, trackBonusCashInfoClick } from '@core/Areas/Bonuses/analytics'

export const BalanceSection: React.FC = () => {
  const dispatch = useAppDispatch()
  const isBalanceVisible = useAppSelector(state => state.appHeader.isBalanceVisible)

  const {
    accountBalance = 0,
    bonusBetBalance = 0,
    bonusCashBalance = 0,
  } = useObservableImmutable(userAccountState$, [
    'accountBalance',
    'bonusBetBalance',
    'bonusCashBalance',
  ])

  const handleToggleBalanceVisibleClick = () => {
    dispatch(toggleIsBalanceVisible())
    trackSideMenuItemClick('hamburger-balance-visibility-toggle')
  }

  const showBonusBetDescription = () => {
    dispatch(setBonusDescriptionToDisplay('bonus_bet'))
    trackBonusBetsInfoClick('Account Dropdown')
  }

  const showBonusCashDescription = () => {
    dispatch(setBonusDescriptionToDisplay('bonus_cash'))
    trackBonusCashInfoClick('Account Dropdown')
  }

  return (
    <BalancesGridStyled>
      <div>
        <Button color='tertiary_grey' size='xs' onClick={handleToggleBalanceVisibleClick}>
          <Icon
            name={isBalanceVisible ? 'CustomVisibilityOff' : 'CustomVisibilityOn'}
            color={hexColors.grey}
            size='1.8rem'
          />
          Balance
        </Button>

        <Money amount={accountBalance || 0} decimalPlaces={!accountBalance ? 0 : 2} />
      </div>

      {!!bonusBetBalance && (
        <div>
          <Button color='tertiary_grey' size='xs' onClick={showBonusBetDescription}>
            Bonus Bets
            <Icon name='LineHelpCircle' size='1.4rem' color={hexColors.grey} />
          </Button>

          <Money amount={bonusBetBalance} decimalPlaces={!bonusBetBalance ? 0 : 2} />
        </div>
      )}

      {!!bonusCashBalance && (
        <div>
          <Button color='tertiary_grey' onClick={showBonusCashDescription} size='xs'>
            Bonus Cash
            <Icon name='LineHelpCircle' size='1.4rem' color={hexColors.grey} />
          </Button>

          <Money amount={bonusCashBalance} decimalPlaces={!bonusCashBalance ? 0 : 2} />
        </div>
      )}
    </BalancesGridStyled>
  )
}

// ======
// Styles
// ======

const BalancesGridStyled = styled.section({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',

  '> div': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.smx2,
    minHeight: '5rem',
    fontFamily: font.family.secondary,
    fontWeight: font.weight.medium,
    fontSize: font.size.lg.fontSize,
    color: hexColors.charCoal,

    '> button:first-of-type': {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: '0.2rem',
      padding: 0,
      fontSize: font.size.md.fontSize,
      height: '1.8rem',
      lineHeight: 1,
      color: hexColors.grey,
      userSelect: 'none',
    },
  },

  '> div:nth-of-type(2), > div:nth-of-type(3)': {
    color: colors.success[600],
  },
})
